/**
 * Quem já entrou NESTE APARELHO — F153.
 *
 * *"Não tinha de salvar usuário, trocar usuário etc., igual sistema mesmo?"*
 *
 * A F151 guardava **um** e-mail. No PC do balcão isso é meio caminho: o dono
 * entra depois da vendedora, o e-mail dela é sobrescrito, e amanhã ela digita
 * o endereço inteiro de novo. E o "trocar de usuário" de lá não trocava —
 * apagava.
 *
 * Aqui mora uma LISTA: quem já entrou neste computador, com o nome que a
 * pessoa reconhece, mais recente primeiro. A tela de login vira um seletor de
 * conta, e trocar de pessoa passa a ser um clique em vez de uma digitação.
 *
 * ## O que NÃO mora aqui, e nunca vai morar
 *
 * **A senha.** Nem aqui, nem em lugar nenhum do navegador. O que permite
 * entrar continua sendo digitado toda vez, e a sessão continua vivendo num
 * cookie httpOnly que este código não consegue ler (ADR-0012). Guardar a lista
 * economiza digitação; não economiza autenticação.
 *
 * E é honesto registrar o preço: esta lista **revela ao balcão quais e-mails
 * usam o sistema neste computador** — o mesmo que uma etiqueta com o nome no
 * crachá. Num balcão é aceitável e é o pedido; num aparelho público não seria,
 * e por isso "Remover deste computador" existe por conta, não só em bloco.
 *
 * ## Sem tenant, e por aparelho
 *
 * Ao contrário de `desktop/layout-storage.ts`, que guarda por usuário e por
 * loja, aqui não há escopo nenhum: a conta é ANTERIOR a tudo isso. É com ela
 * que a pessoa entra, antes de existir sessão que diga de qual loja ela é.
 *
 * ## E por que tudo tem try/catch
 *
 * `localStorage` **lança** em janela anônima e quando o navegador bloqueia
 * dados do site. Uma tela de login que não abre é muito pior que uma que não
 * lembra, então toda falha aqui vira "não lembro de ninguém".
 */

const STORAGE_KEY = 'aeris.accounts'

/**
 * A chave da F151, lida uma última vez.
 *
 * Quem já usou a versão anterior tem o e-mail guardado ali. Ignorá-la faria a
 * melhoria PIORAR a vida dessa pessoa: ela abriria a tela de login vazia
 * depois de semanas com o campo preenchido, e concluiria — com razão — que a
 * atualização quebrou alguma coisa.
 */
const LEGACY_KEY = 'aeris.last-email'

/**
 * Quantas contas cabem.
 *
 * Cinco porque é quanto cabe numa lista que se lê de relance, e porque um PC
 * de loja com mais de cinco pessoas por turno não existe. Passou disso, a mais
 * antiga sai — quem sai é sempre quem há mais tempo não entra, que é também a
 * de quem provavelmente não trabalha mais ali.
 */
const MAX_ACCOUNTS = 5

export type SavedAccount = {
  /** Como a pessoa entra. É a identidade desta lista. */
  readonly email: string
  /**
   * Como a pessoa se reconhece.
   *
   * Vem da sessão, então só existe depois do primeiro login bem-sucedido com
   * esta versão. Vazio é um estado real — uma conta migrada da F151 — e a tela
   * mostra o e-mail no lugar, em vez de inventar um nome.
   */
  readonly displayName: string
  /** Para ordenar. ISO, gravado pelo navegador. */
  readonly lastUsedAt: string
}

const isAccount = (value: unknown): value is SavedAccount =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as SavedAccount).email === 'string' &&
  (value as SavedAccount).email !== '' &&
  typeof (value as SavedAccount).displayName === 'string' &&
  typeof (value as SavedAccount).lastUsedAt === 'string'

const write = (accounts: readonly SavedAccount[]): void => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(accounts))
  } catch {
    // Não lembrar é uma inconveniência; derrubar o login é um balcão parado.
  }
}

/**
 * As contas deste aparelho, da mais recente para a mais antiga.
 *
 * Toda linha estranha é descartada em silêncio, uma a uma: um item corrompido
 * — de uma versão futura, de uma extensão, de uma edição à mão no console —
 * não pode levar junto as contas que estão boas.
 */
export const readAccounts = (): readonly SavedAccount[] => {
  let stored: readonly SavedAccount[] = []
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (raw !== null) {
      const parsed: unknown = JSON.parse(raw)
      if (Array.isArray(parsed)) stored = parsed.filter(isAccount)
    }
  } catch {
    stored = []
  }

  if (stored.length === 0) {
    const legacy = readLegacyEmail()
    if (legacy !== '') {
      return [{ email: legacy, displayName: '', lastUsedAt: '' }]
    }
  }

  return [...stored].sort((a, b) => b.lastUsedAt.localeCompare(a.lastUsedAt))
}

const readLegacyEmail = (): string => {
  try {
    return window.localStorage.getItem(LEGACY_KEY) ?? ''
  } catch {
    return ''
  }
}

/**
 * Registrar quem acabou de entrar.
 *
 * Chamado só DEPOIS de o servidor aceitar (F151): um endereço digitado errado
 * — ou o de quem errou a senha e desistiu — não pode virar uma das cinco caras
 * da tela de amanhã.
 *
 * O e-mail é comparado em minúsculas porque é assim que o servidor o trata
 * (`LoginRequest` faz `toLowerCase`). Sem isso, "Elias@" e "elias@" seriam
 * duas pessoas diferentes nesta lista e a mesma pessoa no banco.
 */
export const rememberAccount = (account: {
  email: string
  displayName: string
}): void => {
  const email = account.email.trim().toLowerCase()
  if (email === '') return

  const next: SavedAccount = {
    email,
    displayName: account.displayName.trim(),
    lastUsedAt: new Date().toISOString(),
  }

  const others = readAccounts().filter((saved) => saved.email !== email)
  write([next, ...others].slice(0, MAX_ACCOUNTS))

  // A chave da F151 já foi absorvida; deixá-la para trás faria a migração
  // acontecer de novo se um dia a lista esvaziar.
  try {
    window.localStorage.removeItem(LEGACY_KEY)
  } catch {
    // idem
  }
}

/** "Remover deste computador" — alguém saiu da loja, ou é um PC emprestado. */
export const forgetAccount = (email: string): void => {
  const target = email.trim().toLowerCase()
  const kept = readAccounts().filter((saved) => saved.email !== target)
  write(kept)
  if (kept.length === 0) {
    try {
      window.localStorage.removeItem(LEGACY_KEY)
    } catch {
      // idem
    }
  }
}
